//文字コードはutf-8

import { BREAKPOINTS } from '../utils/breakpoints.js';
import { LOADER, VIEWPORT, VIDEO, ANIMATION, TOP_KV } from '../utils/constants.js';

/**
 * キービジュアル動画の初期化
 * スーパーリロード時（初回訪問時）：最初から最後まで動画を再生（ループなし）
 * 通常リロード時（2回目以降）：動画を読み込まず、フォールバック画像を表示（パフォーマンス向上のため）
 * @param {Object} options - 設定オプション
 * @param {number} options.introFadeoutDelay - イントロテキストのフェードアウトまでの遅延（ミリ秒、デフォルト: 2000）
 * @param {number} options.textAreaFadeinDelay - テキストエリア表示までの遅延（ミリ秒、デフォルト: 5000）
 * @param {number} options.textAreaDelay - テキストエリア表示までの遅延（ミリ秒、後方互換性のため残す）
 * @param {number} options.loaderFadeoutDelay - ローダーフェードアウト待ち時間（ミリ秒、デフォルト: 500）
 * @param {number} options.loaderTimeout - ローダータイムアウト（ミリ秒、デフォルト: 10000）
 * @param {number} options.viewportMargin - ビューポート監視のマージン（px、デフォルト: 300）
 */
registerForceReplayShortcut();

export function initTopKvVideo(options = {}) {
  // ============================================
  // 設定値（optionsで上書き可能、デフォルトはconstants.jsから取得）
  // ============================================
  const KV_VIDEO_KEY = 'yfc_kv_video_played';
  const INTRO_FADEOUT_DELAY = options.introFadeoutDelay || TOP_KV.INTRO_FADEOUT_DELAY;
  const TEXT_AREA_FADEIN_DELAY = options.textAreaFadeinDelay || TOP_KV.TEXT_AREA_FADEIN_DELAY;
  const TEXT_AREA_DELAY = options.textAreaDelay || TOP_KV.TEXT_AREA_DELAY;
  const LOADER_FADEOUT_DELAY = options.loaderFadeoutDelay || LOADER.FADEOUT_DELAY;
  const LOADER_TIMEOUT = options.loaderTimeout || LOADER.TIMEOUT;
  const VIEWPORT_MARGIN = options.viewportMargin || VIEWPORT.MARGIN;
  const PLAYBACK_FLAG_DELAY = options.playbackFlagDelay || TOP_KV.PLAYBACK_FLAG_DELAY;

  // ============================================
  // DOM要素の取得（jsプレフィックスのクラスを起点に）
  // ============================================
  const videoContainer = document.querySelector('.jsTopKvVideo');
  const videoElement = videoContainer ? videoContainer.querySelector('.jsTopKvVideo__main') : null;
  
  if (!videoElement || !videoContainer) {
    return;
  }

  // 常に動画を再生するため、再生フラグのチェックは行わない
  const introElement = document.querySelector('.jsTopKvIntro');
  const textAreaElement = document.querySelector('.jsTopKvTextArea');
  
  videoContainer.classList.remove('is-hidden');

  // 初回訪問時のみ動画を読み込む
  // SPでの自動再生を確実にするため、必要な属性を先に設定
  videoElement.muted = true;
  videoElement.loop = false;
  videoElement.setAttribute('playsinline', '');
  videoElement.setAttribute('webkit-playsinline', '');
  videoElement.setAttribute('preload', 'metadata');
  
  // 画面幅に応じてPC/SPの動画パスを切り替え
  const isSmartphone = typeof window !== 'undefined' ? window.innerWidth <= BREAKPOINTS.smartphone_max : false;
  const videoPath = isSmartphone
    ? '/wp-content/themes/iwasaki-yfc/fc/assets/video/top/sp/kv.mp4'
    : '/wp-content/themes/iwasaki-yfc/fc/assets/video/top/kv.mp4';
  const posterPath = isSmartphone
    ? '/wp-content/themes/iwasaki-yfc/fc/assets/img/top/sp/img_kv.webp'
    : '/wp-content/themes/iwasaki-yfc/fc/assets/img/top/img_kv.webp';

  // 動画パスを設定
  videoElement.src = videoPath;
  // posterもデバイスに合わせて差し替え
  videoElement.setAttribute('poster', posterPath);
  
  // 動画を明示的に読み込む
  videoElement.load();

  // エラー発生フラグ（重複処理を防ぐ）
  let hasErrorOccurred = false;
  // 動画が終了したことを記録するフラグ（ループを防ぐため）
  let videoHasEnded = false;
  // メタデータが読み込まれ、アニメーションを開始して良いかのフラグ
  let isVideoReadyForAnimation = false;
  // 再生開始処理が重複しないようにするフラグ
  let hasStartedPlayback = false;

  // 動画の読み込みエラー時の処理（パスのリンク切れ、404エラー、ネットワークエラーなどすべてのエラーを検知）
  const handleVideoError = function() {
    if (hasErrorOccurred) {
      return; // 既にエラー処理済みの場合はスキップ
    }
    
    // エラーの詳細を確認
    const error = videoElement.error;
    let errorMessage = '動画を再生できませんでした。';
    
    if (error) {
      // error.code: 1=MEDIA_ERR_ABORTED, 2=MEDIA_ERR_NETWORK, 3=MEDIA_ERR_DECODE, 4=MEDIA_ERR_SRC_NOT_SUPPORTED
      switch (error.code) {
        case 1:
          errorMessage = '動画を再生できませんでした。（読み込みが中断されました）';
          break;
        case 2:
          errorMessage = '動画を再生できませんでした。（ネットワークエラー）';
          break;
        case 3:
          errorMessage = '動画を再生できませんでした。（デコードエラー）';
          break;
        case 4:
          errorMessage = '動画を再生できませんでした。（サポートされていない形式です）';
          break;
        default:
          errorMessage = '動画を再生できませんでした。';
      }
    }
    
    console.warn(errorMessage);
    if (error) {
      console.warn('動画エラー詳細:', error);
    }
    
    hasErrorOccurred = true;
    bypassLoaderWait(videoElement); // ローダー待機を即座に解除
    showFallbackImage(videoElement, videoContainer);
  };
  
  videoElement.addEventListener('error', handleVideoError, { once: true });
  
  // loadedmetadataが一定時間内に発火しない場合もエラーとみなす（動画が読み込めない場合）
  let metadataTimeoutId = null;
  const METADATA_TIMEOUT = 3000; // 3秒
  
  videoContainer.classList.remove('is-hidden');

  // 動画のメタデータが読み込まれたら、動画を表示（poster属性の画像から動画に切り替え）
  const handleMetadataLoaded = function() {
    // メタデータ読み込みタイムアウトをクリア
    if (metadataTimeoutId) {
      clearTimeout(metadataTimeoutId);
      metadataTimeoutId = null;
    }
    
    // エラーが発生している場合は処理をスキップ
    if (hasErrorOccurred) {
      return;
    }
    
    // ループを確実に無効化（動画が読み込まれた後にも再度設定）
    videoElement.loop = false;
    videoElement.removeAttribute('loop');
    // videoHasEndedフラグをリセット（動画が読み込まれた時点ではまだ終了していない）
    videoHasEnded = false;
    isVideoReadyForAnimation = true;
    
    // 必ず先頭フレームから開始するように0秒へ戻して静止表示
    try {
      videoElement.currentTime = 0;
      videoElement.pause();
    } catch (error) {
      // noop
    }
    
    // ポスターを削除し、動画の先頭フレームを表示（ローディング画面が閉じる前に処理）
    // これにより、ローディング画面が閉じた時点で既に動画の先頭フレームが表示されている
    const originalPoster = videoElement.getAttribute('poster');
    if (originalPoster) {
      videoElement.dataset.yfcOriginalPoster = originalPoster;
      videoElement.removeAttribute('poster');
    }
    
    // 動画の先頭フレームを確実に表示
    videoElement.style.opacity = '1';
    
    // 動画の再生準備が完了したことを通知（ローダー待ち用）
    // data属性を設定して、ローダーが確認できるようにする
    videoElement.dataset.yfcVideoReady = 'true';
    // カスタムイベントも発火
    try {
      videoElement.dispatchEvent(new Event('yfcVideoReady', { bubbles: true }));
    } catch (error) {
      // noop
    }
  };
  
  // メタデータ読み込みの監視を設定
  if (videoElement.readyState >= 1) {
    // 既にメタデータが読み込まれている場合
    handleMetadataLoaded();
  } else {
    videoElement.addEventListener('loadedmetadata', handleMetadataLoaded, { once: true });
  }
  
  // タイムアウト処理：一定時間内にメタデータが読み込まれない場合はエラーとみなす
  metadataTimeoutId = setTimeout(function() {
    if (!hasErrorOccurred && (!videoElement.duration || isNaN(videoElement.duration))) {
      console.warn('動画を再生できませんでした。（メタデータの読み込みがタイムアウトしました）');
      handleVideoError();
    }
  }, METADATA_TIMEOUT);

  // タイマーIDを保存（ビューポート監視でクリア/再設定するため）
  const timerRefs = {
    introFadeout: null,
    textAreaFadein: null,
    videoStart: null
  };
  // introの再表示を防ぐための状態管理
  const introState = {
    shown: false,
    finished: false
  };

  // ローダーが消えるのを待ってから動画を再生
  const startVideoPlayback = function() {
    // ローダーが消えた時点で、動画の先頭フレームが既に表示されていることを確認
    // ポスターは既にメタデータ読み込み完了時に削除済み
    // 先頭フレームは既にメタデータ読み込み完了時に設定済みなので、再設定は不要
    // （再生中にcurrentTimeを変更すると、再生位置が戻ってしまうため）
    
    // 初回訪問時：最初から最後まで再生（ループなし）
    // 1. 動画を最初から再生
    // 実際の動画再生開始時点（playingイベント）を検知してからタイマーを開始
    const handleVideoPlaying = function() {
      // エラーが発生している場合は処理をスキップ
      if (hasErrorOccurred) {
        return;
      }
      // メタデータが未読の状態ではタイマーを動かさない
      if (!isVideoReadyForAnimation) {
        return;
      }
      // ポスターは既にメタデータ読み込み完了時に削除済み
      // 先頭フレームは既に表示されているので、opacityのみ確認
      videoElement.style.opacity = '1';
      // 1. introを最初に表示（まだ表示していない場合のみ）
      const introFinishedAttr = introElement && introElement.dataset.introFinished === 'true';
      if (introElement && !introState.shown && !introState.finished && !introFinishedAttr) {
        introElement.classList.add('is-visible');
        introState.shown = true;
      }
      
      // 動画が既に終了している場合は処理をスキップ（currentTime変更による誤検知を防ぐ）
      if (videoHasEnded) {
        // introを確実に非表示にする
        if (introElement) {
          introElement.classList.remove('is-visible');
        }
        // textAreaを確実に表示する
        if (textAreaElement) {
          textAreaElement.classList.add('is-visible');
        }
        return;
      }
      
      // 動画が実際に再生を開始した時刻を記録
      timerRefs.videoStart = Date.now();
      
      // 3. 2秒後にintroをフェードアウト
      timerRefs.introFadeout = setTimeout(function() {
        if (introElement) {
          introElement.classList.remove('is-visible');
          introElement.classList.add('is-intro-finished');
          introState.finished = true;
          introElement.dataset.introFinished = 'true';
        }
        timerRefs.introFadeout = null;
      }, INTRO_FADEOUT_DELAY);
      
      // 4. 6秒後にtextAreaをフェードイン
      timerRefs.textAreaFadein = setTimeout(function() {
        if (textAreaElement) {
          textAreaElement.classList.add('is-visible');
        }
        timerRefs.textAreaFadein = null;
      }, TEXT_AREA_FADEIN_DELAY);
      
      // playingイベントは一度だけ実行
      videoElement.removeEventListener('playing', handleVideoPlaying);
      };
    
    // playingイベントを監視（実際の再生開始時点を検知）
    videoElement.addEventListener('playing', handleVideoPlaying, { once: true });
    
    // 動画の再生を開始（SPでも確実に再生されるように）
    const startPlayback = function() {
      // 二重起動防止
      if (hasStartedPlayback) {
        return;
      }
      hasStartedPlayback = true;
      // エラーが発生している場合は再生しない
      if (hasErrorOccurred) {
        return;
      }
      
      // SPでの自動再生を確実にするため、属性を再確認・再設定
      videoElement.muted = true;
      videoElement.setAttribute('muted', '');
      videoElement.setAttribute('playsinline', '');
      videoElement.setAttribute('webkit-playsinline', '');
      
      // メタデータが読み込まれていることを再確認
      if (videoElement.readyState < 1) {
        // メタデータがまだ読み込まれていない場合は待つ
        videoElement.addEventListener('loadedmetadata', function() {
          if (!hasErrorOccurred) {
            attemptPlay();
          }
        }, { once: true });
        return;
      }
      
      attemptPlay();
      
      function attemptPlay() {
        if (hasErrorOccurred) {
          return;
        }
        // 再生前に必ず0秒から開始（前回位置の残りを防止）
        try {
          videoElement.currentTime = 0;
          videoHasEnded = false;
        } catch (error) {
          // noop
        }
        
        // 動画の再生を開始
        const playPromise = videoElement.play();
        
        if (playPromise !== undefined) {
          playPromise.then(function() {
            // 再生開始成功（playingイベントでタイマーが開始される）
          }).catch(function(error) {
            // 再生エラー時の処理
            console.warn('動画を再生できませんでした。', error);
            if (!hasErrorOccurred) {
              hasErrorOccurred = true;
              showFallbackImage(videoElement, videoContainer);
            }
            // エラー時はplayingイベントリスナーを削除
            videoElement.removeEventListener('playing', handleVideoPlaying);
          });
        } else {
          // Promiseが返されない場合（古いブラウザなど）は直接再生を試みる
          try {
            videoElement.play();
          } catch (error) {
            console.warn('動画を再生できませんでした。', error);
            if (!hasErrorOccurred) {
              hasErrorOccurred = true;
              showFallbackImage(videoElement, videoContainer);
            }
          }
        }
      }
    };
    
    // メタデータが読み込まれている場合は即座に再生、そうでない場合は待つ
    // PC/SPで同じ挙動にするため、追加のディレイは設けない
    const waitForMetadataAndPlay = function() {
      if (videoElement.readyState >= 1 && isVideoReadyForAnimation) {
        // HAVE_METADATA以上（メタデータが読み込まれている）かつアニメーション準備完了
        startPlayback();
      } else {
        // メタデータが読み込まれるまで待つ（isVideoReadyForAnimationフラグも確認）
        const checkAndPlay = function() {
          if (videoElement.readyState >= 1 && isVideoReadyForAnimation) {
            startPlayback();
          } else {
            // メタデータ読み込みとアニメーション準備完了を待つ
            const metadataHandler = function() {
              // PC/SPで同じ挙動にするため、追加のディレイは設けない
              if (!hasErrorOccurred && videoElement.readyState >= 1 && isVideoReadyForAnimation) {
                startPlayback();
              }
            };
            videoElement.addEventListener('loadedmetadata', metadataHandler, { once: true });
          }
        };
        checkAndPlay();
      }
    };
    waitForMetadataAndPlay();
    
    // 動画が最後まで再生されたら停止し、再生フラグを設定
    const handleVideoEnded = function() {
      videoHasEnded = true;
      
      // playingイベントリスナーを削除（currentTime変更による誤検知を防ぐ）
      videoElement.removeEventListener('playing', handleVideoPlaying);
      
      // タイマーをクリア（動画が終了したので、アニメーションタイマーは不要）
      if (timerRefs.introFadeout !== null) {
        clearTimeout(timerRefs.introFadeout);
        timerRefs.introFadeout = null;
      }
      if (timerRefs.textAreaFadein !== null) {
        clearTimeout(timerRefs.textAreaFadein);
        timerRefs.textAreaFadein = null;
      }
      
      // 動画が最後まで再生されたら、明示的に停止
      videoElement.pause();
      // ループを確実に無効化
      videoElement.loop = false;
      videoElement.removeAttribute('loop');
      // currentTimeを最後の位置に固定（ループを防ぐ）
      // 最後のフレームを確実に表示するため、duration - 0.1秒に設定
      if (videoElement.duration && !isNaN(videoElement.duration)) {
        // 最後のフレームを表示するため、durationより少し前の位置に設定
        const lastFrameTime = Math.max(0, videoElement.duration - 0.1);
        videoElement.currentTime = lastFrameTime;
      }
      
      // introを確実に非表示にする（タイマーが実行されなかった場合のフォールバック）
      if (introElement) {
        introElement.classList.remove('is-visible');
        introElement.classList.add('is-intro-finished');
        introElement.dataset.introFinished = 'true';
      }
      // textAreaを確実に表示する（タイマーが実行されなかった場合のフォールバック）
      if (textAreaElement) {
        textAreaElement.classList.add('is-visible');
      }
      
      // poster属性を削除したまま維持（静止画に差し替えない）
      // 常に再生するため、再生フラグの設定は不要
      // setPlaybackFlag(KV_VIDEO_KEY_TAB);
    };
    videoElement.addEventListener('ended', handleVideoEnded, { once: true });
    
    // 動画が終了後に再生されようとした場合に停止
    const handleVideoPlay = function() {
      if (videoHasEnded) {
        videoElement.pause();
        // 最後のフレームを表示し続ける
        if (videoElement.duration && !isNaN(videoElement.duration)) {
          const lastFrameTime = Math.max(0, videoElement.duration - 0.1);
          videoElement.currentTime = lastFrameTime;
        }
        // introを確実に非表示にする（動画終了後の誤検知を防ぐ）
        if (introElement) {
          introElement.classList.remove('is-visible');
        }
        // textAreaを確実に表示する
        if (textAreaElement) {
          textAreaElement.classList.add('is-visible');
        }
      }
    };
    videoElement.addEventListener('play', handleVideoPlay);
    
    // timeupdateイベントでも、動画が終了位置に達した場合は停止（念のため）
    const handleTimeUpdate = function() {
      if (videoHasEnded) {
        videoElement.pause();
        // 最後のフレームを表示し続ける（静止画に差し替えない）
        if (videoElement.duration && !isNaN(videoElement.duration)) {
          const lastFrameTime = Math.max(0, videoElement.duration - 0.1);
          videoElement.currentTime = lastFrameTime;
        }
        return;
      }
      
      if (videoElement.duration && !isNaN(videoElement.duration)) {
        const currentTime = videoElement.currentTime;
        const duration = videoElement.duration;
        // 動画が最後まで到達した場合（少しの誤差を許容）
        if (currentTime >= duration - 0.1) {
          videoHasEnded = true;
          
          // playingイベントリスナーを削除（currentTime変更による誤検知を防ぐ）
          videoElement.removeEventListener('playing', handleVideoPlaying);
          
          // タイマーをクリア（動画が終了したので、アニメーションタイマーは不要）
          if (timerRefs.introFadeout !== null) {
            clearTimeout(timerRefs.introFadeout);
            timerRefs.introFadeout = null;
          }
          if (timerRefs.textAreaFadein !== null) {
            clearTimeout(timerRefs.textAreaFadein);
            timerRefs.textAreaFadein = null;
          }
          
          videoElement.pause();
          videoElement.loop = false;
          videoElement.removeAttribute('loop');
          // 最後のフレームを表示するため、duration - 0.1秒に設定（静止画に差し替えない）
          const lastFrameTime = Math.max(0, duration - 0.1);
          videoElement.currentTime = lastFrameTime;
          
          // introを確実に非表示にする（タイマーが実行されなかった場合のフォールバック）
          if (introElement) {
            introElement.classList.remove('is-visible');
          }
          // textAreaを確実に表示する（タイマーが実行されなかった場合のフォールバック）
          if (textAreaElement) {
            textAreaElement.classList.add('is-visible');
          }
          
          // 常に再生するため、再生フラグの設定は不要
          // if (!getPlaybackFlag(KV_VIDEO_KEY_TAB)) {
          //   setPlaybackFlag(KV_VIDEO_KEY_TAB);
          // }
        }
      }
    };
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    
    // seekedイベントでも、動画が終了位置を超えた場合は戻す
    const handleVideoSeeked = function() {
      if (videoHasEnded) {
        // 最後のフレームを表示し続ける（静止画に差し替えない）
        if (videoElement.duration && !isNaN(videoElement.duration)) {
          const lastFrameTime = Math.max(0, videoElement.duration - 0.1);
          videoElement.currentTime = lastFrameTime;
        }
        videoElement.pause();
        return;
      }
      
      if (videoElement.duration && !isNaN(videoElement.duration)) {
        const currentTime = videoElement.currentTime;
        const duration = videoElement.duration;
        // 動画が最後まで到達した場合
        if (currentTime >= duration - 0.1) {
          videoHasEnded = true;
          
          // playingイベントリスナーを削除（currentTime変更による誤検知を防ぐ）
          videoElement.removeEventListener('playing', handleVideoPlaying);
          
          // タイマーをクリア（動画が終了したので、アニメーションタイマーは不要）
          if (timerRefs.introFadeout !== null) {
            clearTimeout(timerRefs.introFadeout);
            timerRefs.introFadeout = null;
          }
          if (timerRefs.textAreaFadein !== null) {
            clearTimeout(timerRefs.textAreaFadein);
            timerRefs.textAreaFadein = null;
          }
          
          videoElement.pause();
          videoElement.loop = false;
          videoElement.removeAttribute('loop');
          // 最後のフレームを表示するため、duration - 0.1秒に設定（静止画に差し替えない）
          const lastFrameTime = Math.max(0, duration - 0.1);
          videoElement.currentTime = lastFrameTime;
          
          // introを確実に非表示にする（タイマーが実行されなかった場合のフォールバック）
          if (introElement) {
            introElement.classList.remove('is-visible');
          }
          // textAreaを確実に表示する（タイマーが実行されなかった場合のフォールバック）
          if (textAreaElement) {
            textAreaElement.classList.add('is-visible');
          }
          
          // 常に再生するため、再生フラグの設定は不要
          // if (!getPlaybackFlag(KV_VIDEO_KEY)) {
          //   setPlaybackFlag(KV_VIDEO_KEY);
          // }
        }
      }
    };
    videoElement.addEventListener('seeked', handleVideoSeeked);
    
    // ページの可視性を監視して、タブが再表示されたときに動画を再開
    const handleVisibilityChange = function() {
      // ページが再表示されたとき
      if (!document.hidden) {
        // 動画が既に終了している場合は再開しない
        if (videoHasEnded) {
          return;
        }
        // エラーが発生している場合は再開しない
        if (hasErrorOccurred) {
          return;
        }
        // 動画が一時停止している場合のみ再開
        if (videoElement.paused && !videoElement.ended && !videoElement.error) {
          // メタデータが読み込まれていることを確認
          if (videoElement.readyState >= 1) {
            const playPromise = videoElement.play();
            if (playPromise !== undefined) {
              playPromise.catch(function(error) {
                // 再生エラーは無視（ユーザー操作による停止など）
                console.warn('動画の再開に失敗しました:', error);
              });
            }
          }
        }
      }
    };
    
    // visibilitychangeイベントを監視
    document.addEventListener('visibilitychange', handleVisibilityChange);
  };

  // ローダーの状態を確認（初回訪問時のみ動画を再生するため、ローダー処理も初回のみ）
  const loader = document.querySelector(LOADER.SELECTOR);
  if (loader) {
    // ローダーが既に消えている場合（display: none）
    if (loader.style.display === 'none' || loader.classList.contains('is-fadeout')) {
      // フェードアウトアニメーション完了を待つ
      setTimeout(startVideoPlayback, LOADER_FADEOUT_DELAY);
    } else {
      // ローダーが消えるのを待つ
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (loader.classList.contains('is-fadeout')) {
              observer.disconnect();
              // フェードアウトアニメーション完了を待つ
              setTimeout(startVideoPlayback, LOADER_FADEOUT_DELAY);
            }
          }
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            if (loader.style.display === 'none') {
              observer.disconnect();
              // フェードアウトアニメーション完了を待つ
              setTimeout(startVideoPlayback, LOADER_FADEOUT_DELAY);
            }
          }
        });
      });
      
      observer.observe(loader, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
      
      // タイムアウト対策：指定時間経過したら強制的に再生開始
      setTimeout(function() {
        observer.disconnect();
        startVideoPlayback();
      }, LOADER_TIMEOUT);
    }
  } else {
    // ローダーが存在しない場合は即座に再生
    startVideoPlayback();
  }
  
  // 常に再生するため、再生フラグの設定は不要
  // setTimeout(function() {
  //   if (!getPlaybackFlag(KV_VIDEO_KEY_TAB)) {
  //     setPlaybackFlag(KV_VIDEO_KEY_TAB);
  //   }
  // }, PLAYBACK_FLAG_DELAY);

  // ビューポート監視は無効化（動画は常に再生を続ける）
  // setupViewportObserver(videoContainer, videoElement, VIEWPORT_MARGIN, timerRefs, introElement, textAreaElement, INTRO_FADEOUT_DELAY, TEXT_AREA_FADEIN_DELAY);
}

/**
 * ビューポートから一定距離離れたら動画を一時停止する監視を設定
 * @param {HTMLElement} container - 動画コンテナ
 * @param {HTMLVideoElement} video - 動画要素
 * @param {number} margin - ビューポート監視のマージン（px）
 * @param {Object} timerRefs - タイマーIDを保持するオブジェクト（参照渡し）
 * @param {HTMLElement|null} introElement - intro要素
 * @param {HTMLElement|null} textAreaElement - textArea要素
 * @param {number} introFadeoutDelay - introフェードアウト遅延（ミリ秒）
 * @param {number} textAreaFadeinDelay - textAreaフェードイン遅延（ミリ秒）
 */
function setupViewportObserver(container, video, margin, timerRefs, introElement, textAreaElement, introFadeoutDelay, textAreaFadeinDelay) {
  if (!container || !video || !('IntersectionObserver' in window)) {
    return;
  }

  const observer = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      // ビューポートから指定距離以上離れている場合（isIntersectingがfalse）
      if (!entry.isIntersecting) {
        // 動画を一時停止
        if (!video.paused) {
          video.pause();
        }
        
        // タイマーをクリア（動画が停止したので、アニメーションも停止）
        if (timerRefs.introFadeout !== null) {
          clearTimeout(timerRefs.introFadeout);
          timerRefs.introFadeout = null;
        }
        if (timerRefs.textAreaFadein !== null) {
          clearTimeout(timerRefs.textAreaFadein);
          timerRefs.textAreaFadein = null;
        }
      } else {
        // ビューポート内に戻ってきた場合、動画を再開
        // ただし、動画が最後まで再生されて終了している場合は再開しない
        if (video.paused && !video.error && !video.ended) {
          // エラーが発生しておらず、まだ終了していない場合のみ再生
          video.play().then(function() {
            // 動画が再開されたら、タイマーを再設定
            if (timerRefs.videoStart !== null) {
              const elapsed = Date.now() - timerRefs.videoStart;
              
              // introフェードアウトタイマーを再設定
              if (elapsed < introFadeoutDelay && introElement) {
                const remaining = introFadeoutDelay - elapsed;
                timerRefs.introFadeout = setTimeout(function() {
                  if (introElement && !video.paused) {
                    introElement.classList.remove('is-visible');
                  }
                  timerRefs.introFadeout = null;
                }, remaining);
              } else if (introElement) {
                // 既に時間が経過している場合は即座に非表示
                introElement.classList.remove('is-visible');
              }
              
              // textAreaフェードインタイマーを再設定
              if (elapsed < textAreaFadeinDelay && textAreaElement) {
                const remaining = textAreaFadeinDelay - elapsed;
                timerRefs.textAreaFadein = setTimeout(function() {
                  if (textAreaElement && !video.paused) {
                    textAreaElement.classList.add('is-visible');
                  }
                  timerRefs.textAreaFadein = null;
                }, remaining);
              } else if (textAreaElement) {
                // 既に時間が経過している場合は即座に表示
                textAreaElement.classList.add('is-visible');
              }
            }
          }).catch(function(error) {
            // 再生エラーは無視（ユーザー操作による停止など）
          });
        }
      }
    });
  }, {
    root: null, // ビューポートを基準にする
    rootMargin: `-${margin}px`, // 上下左右指定pxのマージン（範囲外とみなす）
    threshold: 0 // 少しでも交差していればtrue
  });

  observer.observe(container);
}


function getPlaybackFlag(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function setPlaybackFlag(key) {
  try {
    sessionStorage.setItem(key, 'true');
  } catch (error) {
    // sessionStorage write failed
  }
}

function clearPlaybackFlag(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    // sessionStorage remove failed
  }
}

function registerForceReplayShortcut() {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.__yfcKvForceReplayRegistered) {
    return;
  }
  window.__yfcKvForceReplayRegistered = true;

  window.addEventListener('keydown', function(event) {
    const key = (event.key || '').toLowerCase();
    const isReloadKey = key === 'f5' || (key === 'r' && (event.ctrlKey || event.metaKey));
    const hasForceModifier = event.shiftKey;

    if (!isReloadKey || !hasForceModifier) {
      return;
    }

    setForceReplayFlag();
  }, { capture: true });
}

function setForceReplayFlag() {
  try {
    sessionStorage.setItem('yfc_kv_force_replay', '1');
  } catch (error) {
    // sessionStorage write failed
  }
}

function consumeForceReplayFlag() {
  try {
    const flag = sessionStorage.getItem('yfc_kv_force_replay');
    if (flag) {
      sessionStorage.removeItem('yfc_kv_force_replay');
      return true;
    }
  } catch (error) {
    // sessionStorage read failed
  }
  return false;
}

function bypassLoaderWait(videoElement) {
  // data-ignore-loaderを付与し、ローダー待ちをバイパスするカスタムイベントも送出
  if (!videoElement) {
    return;
  }
  try {
    videoElement.setAttribute('data-ignore-loader', 'true');
    videoElement.dispatchEvent(new Event('yfcLoaderBypass'));
  } catch (error) {
    // noop
  }
}

function showFallbackImage(videoElement, videoContainer) {
  // picture要素を優先的に検索（videoタグ内の画像は通常picture要素で囲まれている）
  const pictureElement = videoElement.querySelector('picture');
  const fallbackImage = videoElement.querySelector('[data-fallback-image]') || videoElement.querySelector('img');
  const introElement = document.querySelector('.jsTopKvIntro');
  const textAreaElement = document.querySelector('.jsTopKvTextArea');
  
  // 動画を停止して読み込みを打ち切る
  try {
    videoElement.pause();
    videoElement.removeAttribute('src');
    videoElement.load();
  } catch (error) {
    // noop
  }
  
  // ローダー待ちを解除
  bypassLoaderWait(videoElement);
  
  restoreVideoPoster(videoElement);
  
  // 画像が見つからない場合はコンテナを非表示
  if (!fallbackImage) {
    videoContainer.classList.add('is-hidden');
    return;
  }
  
  // video自体は表示したまま（display: noneにしない）でフォールバック画像を見せる
  videoElement.style.display = '';
  videoElement.style.opacity = '1';
  
  // picture要素が存在する場合は、picture要素を表示
  if (pictureElement) {
    pictureElement.removeAttribute('hidden');
    pictureElement.hidden = false;
    pictureElement.style.display = 'block';
  }
  
  // 画像要素を表示
  fallbackImage.removeAttribute('hidden');
  fallbackImage.hidden = false;
  fallbackImage.style.display = 'block';
  
  // introは非表示（is-visibleクラスを削除）、textAreaのみ表示
  if (introElement) {
    introElement.classList.remove('is-visible');
    introElement.classList.add('is-intro-finished');
    introElement.dataset.introFinished = 'true';
  }
  if (textAreaElement) {
    textAreaElement.classList.add('is-visible');
  }
  
  // フォールバック用のクラスを追加
  videoContainer.classList.add('is-fallback');
}

function restoreVideoPoster(videoElement) {
  const originalPoster = videoElement.dataset.yfcOriginalPoster;
  if (!originalPoster) {
    return;
  }
  videoElement.setAttribute('poster', originalPoster);
}

